import {
    AST,
    ProgDecl,
    PrintStmt,
    ProgCallStmt,
    BoolConst,
    VarExpr,
    BinaryOpExpr,
    UnaryOpExpr,
    FuncExpr,
    NumberConst,
    BinaryOp,
    RepeatStmt,
    WhileStmt,
    IfElseStmt,
    UnaryOp,
    BuiltInCommand,
    ColorExpr,
    ColorConst,
    MakeStmt,
    VarDecl,
    Seq,
    SpecialBuiltInCommand,
} from "./ir/ast";
import { ASTVisitor } from "./ASTVisitor";
import { BuiltInActionSet, DefinedSpecialBuiltIns } from "./ir/builtInCommands";
import { Color } from "./ir/color";
import { RuntimeError } from "./runtimeError";
import { isNumber } from "util";

interface Scope {
    putVariable(variable: VarDecl, value: any): void;
    getVariable(name: string): any;
    getAllVariables(): Map<string, any>;
    addSetOfVariable(variableSet: Map<string, any>): void;
}

export class BasicBlock implements Scope {
    protected definedVariables: Map<string, any>;

    protected currentInstructionIndex: number;

    constructor(
        public readonly Stmts: AST[],
        private _parentBlock?: BasicBlock,
        predefinedVariables?: Map<string, any>,
    ) {
        if (predefinedVariables) {
            this.definedVariables = new Map(predefinedVariables);
        } else {
            this.definedVariables = new Map<string, any>();
        }

        this.currentInstructionIndex = 0;
    }

    public putVariable(variable: VarDecl, value: any) {
        if (!this._internalPutVariable(variable, value)) {
            // Variable does not exist. Define it
            this.definedVariables.set(variable.name.substr(1), value);
        }
    }

    /**
     * _internalPutVariable
     * This function checks if a variable is defined in the current scope and updates it
     * else it calls the parent scope
     * @param variable variable to assign
     * @param value value to assign
     * @returns 'true' if the variable was found in a (parent)scope and was updated
     */
    protected _internalPutVariable(variable: VarDecl, value: any): boolean {
        if (this.definedVariables.get(variable.name.substr(1))) {
            this.definedVariables.set(variable.name.substr(1), value);
            return true;
        }

        if (this.ParentBlock)
            return this.ParentBlock._internalPutVariable(variable, value);

        return false;
    }

    /**
     * Gets the variable if it is defined from this or the parents scope.
     *
     * @param name The name of the variable including the colon (:)
     */
    public getVariable(name: string): any {
        let retVar = this.definedVariables.get(name.substr(1));

        if (retVar === undefined && this.ParentBlock) {
            retVar = this.ParentBlock.getVariable(name);
        }
        return retVar;
    }

    public getAllVariables(): Map<string, any> {
        return new Map(this.definedVariables);
    }

    public addSetOfVariable(variableSet: Map<string, any>) {
        variableSet.forEach((value: any, key: string) =>
            this.definedVariables.set(key, value),
        );
    }

    get CurrentInstruction(): AST | undefined {
        return this.Stmts[this.currentInstructionIndex];
    }

    get ParentBlock(): BasicBlock | undefined {
        return this._parentBlock;
    }

    /**
     * Get the next instruction in the AST sequence.
     *
     * @returns The next AST-instruction or undefined if there are no further AST-instructions.
     */
    nextInstruction(): AST | undefined {
        const curInstr = this.CurrentInstruction;
        if (!curInstr) {
            return undefined;
        }
        this.currentInstructionIndex += 1;
        return curInstr;
    }

    deepCopy(): BasicBlock {
        let parent: BasicBlock | undefined = undefined;
        if (this.ParentBlock) {
            parent = this.ParentBlock.deepCopy();
        }

        const copy = <BasicBlock>Object.create(this);
        copy._parentBlock = parent;
        copy.currentInstructionIndex = this.currentInstructionIndex;
        copy.definedVariables = new Map(this.definedVariables);

        return copy;
    }
}

/**
 * This special BasicBlock is used by the repeat and while statement to control if the body of the statement
 * should be executed again. To distinguish between a normal call and a recurrent call a different representation is used.
 */
export class ControlBlock extends BasicBlock {
    /** Used for the repeat statement to keep track how many times to execute the sequence. Can be anything.*/
    public blockInfo: any;

    private repeatBlockExecution: (block: ControlBlock) => boolean;

    /**
     * This is a special BasicBlock used to control whether to execute the body of a loop again.
     * @param shouldExecuteAgain The callback that decides whether the loop should be executed again. It is called once the whole loop body
     *                              was executed. If true is returned the loop is executed again, else the loop finishes and the instruction
     *                              after the loop will be fetched.
     * @param stmts The statement that wishes to control the execution flow
     * @param ParentBlock The block that called the statement
     * @param parentScope The variable scope of the parent
     */
    constructor(
        shouldExecuteAgain: (block: ControlBlock) => boolean,
        stmts: AST[],
        ParentBlock?: BasicBlock,
    ) {
        super(stmts, ParentBlock);
        this.repeatBlockExecution = shouldExecuteAgain;
    }

    get CurrentInstruction(): AST | undefined {
        const curInstr = super.CurrentInstruction;
        if (!curInstr) {
            if (this.repeatBlockExecution(this)) {
                this.currentInstructionIndex = 0;
                return super.CurrentInstruction;
            }
        }
        return curInstr;
    }

    deepCopy(): ControlBlock {
        const copy = <ControlBlock>super.deepCopy();
        copy.blockInfo = this.blockInfo;

        return copy;
    }
}

/**
 * This BasicBlock type is used to call a new program. This block does not access the parent block to look for variables.
 */
export class CallBlock extends BasicBlock {
    constructor(Stmts: AST[], _parentBlock?: BasicBlock) {
        super(Stmts, _parentBlock);
    }

    protected _internalPutVariable(variable: VarDecl, value: any): boolean {
        if (this.definedVariables.get(variable.name.substr(1))) {
            this.definedVariables.set(variable.name.substr(1), value);
            return true;
        }

        return false;
    }

    public getVariable(name: string): any {
        let retVar = this.definedVariables.get(name.substr(1));

        return retVar;
    }
}

export class XLogoInterpreter extends ASTVisitor<
    BasicBlock,
    BasicBlock | undefined
> {
    private definedProgs: Map<string, ProgDecl> = new Map<string, ProgDecl>();
    private exprEvaluator: ExprEvaluator;
    private currentBlock: BasicBlock | undefined;
    private predefinedVariables: Map<string, any> = new Map<string, any>();

    private _isPaused: boolean = false;
    private pausedMiliseconds: number = -1;
    private pausedDate: number = 0;

    private finishedExecution: boolean;

    /**
     * The Interpreter for the XLogo language.
     * @param input AST that should be executed.
     * @param actionSet Set that define the functions that should be called for the built-in commands
     * @param definedPrograms A map from program names to the corresponding AST for all programs that exist.
     */
    constructor(
        private input: AST,
        private currentActionSet: BuiltInActionSet,
        definedPrograms?: Map<string, ProgDecl>,
        predefinedVariables?: Map<string, any>,
    ) {
        super();
        this.exprEvaluator = new ExprEvaluator();
        this.finishedExecution = false;
        // Needs to be here to have the empty map as default
        if (definedPrograms) this.definedProgs = definedPrograms;
        if (predefinedVariables) this.predefinedVariables = predefinedVariables;
    }

    public get State(): BasicBlock {
        return this.currentBlock;
    }

    public set State(state: BasicBlock) {
        this.currentBlock = state;
        this.finishedExecution = false;
    }

    /**
     * Tells if the interpreter is still running.
     */
    public isRunning(): boolean {
        return !this.finishedExecution;
    }

    /**
     * Executes the next instruction.
     *
     * Limitation: Each call of this method executes the next statement. For a repeat or while statement the repeat and while itself is
     * also a separate call. this means that it needs an additional call to this method to execute the fist instruction in the body of the
     * repeat or while statement. The same holds for program calls. This means that very slow execution that just waits between each call
     * Will experience longer waits when a repeat, while statement or program call is executed.
     * A solution could be to give the caller access to the executed instruction and let them decide whether to call again or wait.
     * Basically every statement that does not draw or move the turtle is such a delay. But it is not responsibility of the interpreter to
     * solve this issue.
     */
    public continueExecution(replaceBlock?: BasicBlock) {
        if (replaceBlock) {
            this.currentBlock = replaceBlock;
            this.finishedExecution = false;
        }

        if (!this.currentBlock) {
            // Create first basic block. It is expected that this.input is a seqence (Seq)
            this.currentBlock = this.visit(this.input, undefined);
            this.currentBlock.addSetOfVariable(this.predefinedVariables);
            this.finishedExecution = false;
            this.currentActionSet.executionStarted();
            return this.currentBlock;
        }

        if (this.finishedExecution) return;

        if (this._isPaused) {
            if (Date.now() < this.pausedDate + this.pausedMiliseconds) {
                return;
            }
            this._isPaused = false;
            this.pausedMiliseconds = -1;
        }

        let curInstr = this.currentBlock.nextInstruction();

        if (!curInstr) {
            // Go back in block stack to find next instruction
            while (
                !this.currentBlock.CurrentInstruction &&
                this.currentBlock.ParentBlock
            ) {
                this.currentBlock = this.currentBlock.ParentBlock;
            }

            if (!this.currentBlock.CurrentInstruction) {
                // Still no instruction. This means that the current block does not have a parent and we're finished.
                this.stopExecution();
            }
            // We return here to enable the debugger to properly see every instruction.
            return;
        }
        // Execute Instruction
        try {
            const retBlock = this.visit(curInstr, this.currentBlock);
            if (retBlock) this.currentBlock = retBlock;
        } catch (e) {
            console.error(
                "A error occurred during interpretation. " + e.message,
            );
            this.stopExecution();
            throw e;
        }
    }

    public stopExecution() {
        if (!this.finishedExecution) {
            this.finishedExecution = true;
            this.currentActionSet.executionFinished();
        }
    }

    public pauseExecution(milliseconds: number) {
        this.pausedMiliseconds = milliseconds;
        this._isPaused = true;
        this.pausedDate = Date.now();
    }

    public isPaused(): boolean {
        return this._isPaused;
    }

    public getPausedMilliseconds(): number {
        return this.pausedMiliseconds;
    }

    protected defaultResult(): BasicBlock | undefined {
        return undefined;
    }

    public visitSeq(ast: Seq, block: BasicBlock): BasicBlock | undefined {
        return new BasicBlock(ast.getChildren(), block);
    }

    public visitRepeatStmt(
        ast: RepeatStmt,
        block: BasicBlock,
    ): BasicBlock | undefined {
        const repeat = this.exprEvaluator.visit(ast.repeatCount, block);
        const repeatBlock = new ControlBlock(
            this.repeatStmtCallback.bind(this),
            ast.loopBody.getChildren(),
            block,
        );
        repeatBlock.blockInfo = repeat;
        return repeatBlock;
    }

    private repeatStmtCallback(block: ControlBlock): boolean {
        // block.blockInfo is the counter how many times to execute the repeat-loop
        block.blockInfo = block.blockInfo - 1;
        if (block.blockInfo <= 0) {
            return false;
        }
        return true;
    }

    public visitWhileStmt(
        ast: WhileStmt,
        block: BasicBlock,
    ): BasicBlock | undefined {
        // TODO: Type check: must be boolean
        if (!this.exprEvaluator.visit(ast.condition, block)) {
            return undefined;
        }

        const whileBlock = new ControlBlock(
            this.whileStmtCallback.bind(this),
            ast.loopBody.getChildren(),
            block,
        );
        whileBlock.blockInfo = ast.condition;
        return whileBlock;
    }

    private whileStmtCallback(block: ControlBlock): boolean {
        // TODO: Type check: must be boolean
        // block.blockInfo is the condition of the while statement and has to be reevaluated every iteration.
        if (!this.exprEvaluator.visit(block.blockInfo, block)) {
            return false;
        }
        return true;
    }

    public visitIfElseStmt(
        ast: IfElseStmt,
        block: BasicBlock,
    ): BasicBlock | undefined {
        // TODO: type check: must be boolean
        const condition = this.exprEvaluator.visit(ast.condition, block);
        if (condition) {
            return new BasicBlock(ast.thenBlock.getChildren(), block);
        } else if (ast.elseBlock) {
            return new BasicBlock(ast.elseBlock.getChildren(), block);
        }
    }

    public visitMakeStmt(
        ast: MakeStmt,
        block: BasicBlock,
    ): BasicBlock | undefined {
        const result = this.exprEvaluator.visit(ast.value, block);
        block.putVariable(ast.declName, result);
        return undefined;
    }

    public visitPrintStmt(
        ast: PrintStmt,
        block: BasicBlock,
    ): BasicBlock | undefined {
        const argExpr = ast.argExpr();
        let arg = ast.argString();
        if (argExpr) {
            arg = this.exprEvaluator.visit(argExpr, block);
        }
        this.currentActionSet.printAction(arg);
        return undefined;
    }

    public visitProgCallStmt(
        ast: ProgCallStmt,
        block: BasicBlock,
    ): BasicBlock | undefined {
        const progToCall = this.definedProgs.get(ast.progName);
        if (!progToCall) {
            // If the semantic verifier does it's job right and no programs that contain errors are passed to the interpreter, then this should never happen
            throw new RuntimeError(
                "Tried to call a program that does not exist.",
                ast,
                "runtime_error_unknown_program",
                { program: ast.progName },
            );
        }

        const callBlock = new CallBlock(progToCall.body.getChildren(), block);

        // Put all parameters with the corresponding value into the scope for the called program
        for (let i = 0; i < progToCall.args.length; i++) {
            const param = this.exprEvaluator.visit(ast.getChildren()[i], block);
            callBlock.putVariable(progToCall.args[i], param);
        }

        return callBlock;
    }

    public visitBuiltInCommand(
        ast: BuiltInCommand,
        block: BasicBlock,
    ): BasicBlock | undefined {
        const args: any[] = [];
        // Visit arguments for builtin command
        ast.getChildren().forEach(arg =>
            args.push(this.exprEvaluator.visit(arg, block)),
        );

        // Lookup function to call for builtin command
        const actionFunc = this.currentActionSet.getActionForCommand(
            ast.command,
        );
        // Call loaded function with arguments for builtin command
        try {
            actionFunc(args);
        } catch (err) {
            if (err instanceof RuntimeError) {
                err.ast = ast;
                throw err;
            }
            // Catch bad designed JavaScript errors
            if (!(err instanceof Error)) {
                throw new RuntimeError(err, ast);
            }
            throw err;
        }

        return undefined;
    }

    public visitSpecialBuiltInCommand(
        ast: SpecialBuiltInCommand,
        block: BasicBlock,
    ): BasicBlock | undefined {
        switch (ast.command) {
            case DefinedSpecialBuiltIns.STOP:
                // TODO: Not nice
                let curBlock: BasicBlock = this.currentBlock;
                while (curBlock) {
                    if (!curBlock.ParentBlock) {
                        this.stopExecution();
                        return;
                    }

                    if (
                        curBlock instanceof ControlBlock ||
                        curBlock instanceof CallBlock
                    ) {
                        this.currentBlock = curBlock.ParentBlock;
                        break;
                    }
                    curBlock = curBlock.ParentBlock;
                }
                return;
            case DefinedSpecialBuiltIns.STOPALL:
                this.stopExecution();
                return;
            case DefinedSpecialBuiltIns.WAIT:
                const waitTime = this.exprEvaluator.visit(
                    ast.getChildren()[0],
                    block,
                );
                // The argument is in hundredths of seconds not in milliseconds so we have to multiply by 10
                if (waitTime < 0)
                    throw new RuntimeError(
                        "Invalid Argument for wait",
                        ast,
                        "runtime_error_wait_negative_number",
                    );
                this.pauseExecution(waitTime * 10);
                return;
            default:
                // This should never happen
                throw new RuntimeError(
                    "Unknown special builtin command",
                    ast,
                    "runtime_error_unknown_builtin",
                    { command: ast.commandName },
                );
        }
    }
}

// To keep things separated the interpreter uses internally a second visitor that actually visits the nodes
class ExprEvaluator extends ASTVisitor<Scope, any> {
    protected defaultResult() {}

    public visitBinaryOpExpr(ast: BinaryOpExpr, scope: Scope) {
        const left = this.visit(ast.left, scope);
        const right = this.visit(ast.right, scope);

        // TODO: Type check
        switch (ast.operator) {
            case BinaryOp.B_TIMES:
                return left * right;
            case BinaryOp.B_DIV:
                if (right === 0)
                    throw new RuntimeError(
                        "Division by 0 not allowed",
                        ast,
                        "runtime_error_division_by_zero",
                    );
                return left / right;
            case BinaryOp.B_MOD:
                if (right === 0)
                    throw new RuntimeError(
                        "Division by 0 not allowed",
                        ast,
                        "runtime_error_division_by_zero",
                    );
                return left % right;
            case BinaryOp.B_PLUS:
                return left + right;
            case BinaryOp.B_MINUS:
                return left - right;

            case BinaryOp.B_EQUAL:
                if (left instanceof Color) {
                    return left.equals(right);
                } else if (right instanceof Color) {
                    return right.equals(left);
                } else {
                    return left === right;
                }
            case BinaryOp.B_NOT_EQUAL:
                return left !== right;
            case BinaryOp.B_LESS_THAN:
                return left < right;
            case BinaryOp.B_LESS_OR_EQUAL:
                return left <= right;
            case BinaryOp.B_GREATER_THAN:
                return left > right;
            case BinaryOp.B_GREATER_OR_EQUAL:
                return left >= right;

            case BinaryOp.B_OR:
                return left || right;
            case BinaryOp.B_AND:
                return left && right;

            default:
                // This should never happen
                throw new RuntimeError(
                    "Invalid operator '" + ast.operator + "'",
                    ast,
                    "runtime_error_unknown_operator",
                    { operator: ast.operator },
                );
        }
    }

    public visitUnaryOpExpr(ast: UnaryOpExpr, scope: Scope) {
        const arg = this.visit(ast.arg, scope);

        // TODO: Type check
        switch (ast.operator) {
            case UnaryOp.U_PLUS:
                return arg;
            case UnaryOp.U_MINUS:
                return -arg;
            case UnaryOp.U_BOOL_NOT:
                return !arg;
            default:
                throw new RuntimeError(
                    "Invalid operator '" + ast.operator + "'",
                    ast,
                    "runtime_error_unknown_operator",
                    { operator: ast.operator },
                );
        }
    }

    public visitFuncExpr(ast: FuncExpr, scope: Scope) {
        // If you add a function to the grammar, don't forget to update this
        const args = ast.getChildren();
        if (args.length === 0) {
            switch (ast.name) {
                case "pi":
                    return Math.PI;
                case "e":
                    return Math.E;
                default:
                    throw new RuntimeError(
                        "Unknown no-arg function '" + ast.name + "'.",
                        ast,
                        "runtime_error_unknown_function",
                        { function: ast.name },
                    );
            }
        } else if (args.length === 1) {
            const arg = this.visit(args[0], scope);
            switch (ast.name) {
                case "sin":
                    return Math.sin(arg);
                case "cos":
                    return Math.cos(arg);
                case "tan":
                    if ((arg - 0.5 * Math.PI) % Math.PI === 0) {
                        throw new RuntimeError(
                            "tan: argument not in domain",
                            ast,
                            "runtime_error_invalid_argument_tan",
                        );
                    }
                    return Math.tan(arg);
                case "asin":
                // same as arcsin
                case "arcsin":
                    if (arg < -1 || arg > 1) {
                        throw new RuntimeError(
                            "arcsin: argument out of range",
                            ast,
                            "runtime_error_generic_not_in_range",
                            {
                                program: "arcsin",
                                lower_bound: "-1",
                                upper_bound: "1",
                            },
                        );
                    }
                    return Math.asin(arg);
                case "acos":
                // same as arccos
                case "arccos":
                    if (arg < -1 || arg > 1) {
                        throw new RuntimeError(
                            "arcsin: argument out of range",
                            ast,
                            "runtime_error_generic_not_in_range",
                            {
                                program: "arccos",
                                lower_bound: "-1",
                                upper_bound: "1",
                            },
                        );
                    }
                    return Math.acos(arg);
                case "atan":
                // same as arctan
                case "arctan":
                    return Math.atan(arg);
                case "abs":
                    return Math.abs(arg);
                case "sqrt":
                    if (arg < 0) {
                        throw new RuntimeError(
                            "sqrt: argument is negative",
                            ast,
                            "runtime_error_sqrt_negative_number",
                        );
                    }
                    return Math.sqrt(arg);
                case "rand":
                //same as random
                case "rnd":
                // same as random
                case "random":
                    return Math.floor(Math.random() * arg);
                case "log10":
                // same as log
                case "log":
                    if (arg < 0) {
                        throw new RuntimeError(
                            "log: argument is negative",
                            ast,
                            "runtime_error_log10_negative_number",
                        );
                    }
                    return Math.log10(arg);
                default:
                    throw new RuntimeError(
                        "Unknown one-arg function '" + ast.name + "'.",
                        ast,
                        "runtime_error_unknown_function",
                        { function: ast.name },
                    );
            }
        } else if (args.length === 2) {
            let firstArg = this.visit(args[0], scope);
            let secondArg = this.visit(args[1], scope);
            switch (ast.name) {
                // Switch statement here to simplify adding new functions with two args
                case "mod":
                    if (secondArg === 0)
                        throw new RuntimeError(
                            "Division by 0 not allowed",
                            ast,
                            "runtime_error_mod_by_zero",
                        );
                    return firstArg % secondArg;
                case "pow":
                // same as power
                case "power":
                    return Math.pow(firstArg, secondArg);
                default:
                    throw new RuntimeError(
                        "Unknown two-arg function '" + ast.name + "'.",
                        ast,
                        "runtime_error_unknown_function",
                        { function: ast.name },
                    );
            }
        }
    }

    public visitColorExpr(ast: ColorExpr, scope: Scope) {
        const red = this.visit(ast.red, scope);
        const green = this.visit(ast.green, scope);
        const blue = this.visit(ast.blue, scope);

        if (
            typeof red !== "number" ||
            typeof green !== "number" ||
            typeof blue !== "number"
        ) {
            throw new RuntimeError(
                "Invalid arguments for color",
                ast,
                "runtime_error_invalid_argument_rgb",
                { color: `[${red} ${green} ${blue}]` },
            );
        }

        if (
            red < 0 ||
            red > 255 ||
            green < 0 ||
            green > 255 ||
            blue < 0 ||
            blue > 255
        ) {
            throw new RuntimeError(
                "Color out of range",
                ast,
                "runtime_error_rgb_out_of_range",
                { color: `[${red} ${green} ${blue}]` },
            );
        }

        return new Color(red, green, blue);
    }

    public visitColorConst(ast: ColorConst, scope: Scope) {
        return ast.color;
    }

    public visitBoolConst(ast: BoolConst, scope: Scope) {
        return ast.valueAsBool;
    }

    public visitNumberConst(ast: NumberConst, scope: Scope) {
        return ast.valueAsNumber;
    }

    public visitVarExpr(ast: VarExpr, scope: Scope) {
        return scope.getVariable(ast.name);
    }
}



// WEBPACK FOOTER //
// ./src/app/xlogo-parser/interpreter.ts